import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from "react";
import { Button } from "react-bootstrap";

/**
 * Visite guidée d'un écran : un projecteur se déplace d'un élément à l'autre,
 * avec une bulle qui explique à quoi il sert.
 *
 * Écrit à la main plutôt qu'avec une librairie (react-joyride & co) : le projet
 * est en React 16.8 et le bundle est déjà signalé comme trop gros au build —
 * une dépendance de plus pour ~200 lignes n'était pas justifiée.
 *
 * Chaque étape cible un élément par son attribut `data-visite`, posé dans la
 * page. Une étape dont la cible est absente (colonne vide, board pas encore
 * chargé) est **ignorée** : on ne montre jamais un projecteur dans le vide.
 *
 * Props :
 *  - `etapes` : [{ cible: 'selecteur-data-visite', titre, texte, placement? }]
 *               `placement` vaut 'auto' (défaut), 'haut' ou 'bas'.
 *  - `show`   : affiche la visite
 *  - `onClose`: appelé à la fermeture (croix, Échap, ou fin de la visite)
 */

const COULEUR_PRIMAIRE = "#429853";
const COULEUR_TEXTE = "#1d1d1b";
const MARGE_PROJECTEUR = 6;
const LARGEUR_BULLE = 340;

const rectDe = (element) => {
  const r = element.getBoundingClientRect();
  return {
    top: r.top - MARGE_PROJECTEUR,
    left: r.left - MARGE_PROJECTEUR,
    width: r.width + MARGE_PROJECTEUR * 2,
    height: r.height + MARGE_PROJECTEUR * 2,
  };
};

const VisiteGuidee = ({ etapes = [], show, onClose }) => {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  // Les étapes dont la cible n'existe pas dans le DOM sont retirées au
  // démarrage : une colonne vide ne doit pas produire une étape fantôme.
  // C'est bien un état, pas une ref : le filtrage a lieu après le premier
  // rendu, il doit donc provoquer un nouveau rendu pour afficher la visite.
  const [etapesVisibles, setEtapesVisibles] = useState([]);
  // Hauteur réelle de la bulle, mesurée après rendu : sans elle, impossible de
  // la garder dans l'écran quand elle se place au-dessus de la cible.
  const bulleRef = useRef(null);
  const [hauteurBulle, setHauteurBulle] = useState(180);

  useEffect(() => {
    if (!show) {
      return;
    }
    setEtapesVisibles(
      etapes.filter((e) => !e.cible || document.querySelector(`[data-visite="${e.cible}"]`))
    );
    setIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const etape = etapesVisibles[index];

  /** Recalcule la position du projecteur sur la cible de l'étape courante. */
  const placer = useCallback(() => {
    if (!etape) {
      return;
    }
    if (!etape.cible) {
      setRect(null);
      return;
    }
    const element = document.querySelector(`[data-visite="${etape.cible}"]`);
    if (!element) {
      setRect(null);
      return;
    }
    setRect(rectDe(element));
  }, [etape]);

  // Amène la cible à l'écran puis pose le projecteur. Le léger délai laisse le
  // scroll fluide se terminer, sinon le projecteur se fige sur l'ancienne position.
  useEffect(() => {
    if (!show || !etape) {
      return undefined;
    }
    const element = etape.cible
      ? document.querySelector(`[data-visite="${etape.cible}"]`)
      : null;
    if (element) {
      // On ne défile que si c'est nécessaire, et jamais en centrant une cible
      // plus haute que l'écran : une colonne fait toute la hauteur du board,
      // la centrer enverrait l'utilisateur tout en bas, en-tête perdu.
      const r = element.getBoundingClientRect();
      const hautDejaVisible = r.top >= 0 && r.top <= window.innerHeight * 0.5;
      const entierementVisible = r.top >= 0 && r.bottom <= window.innerHeight;
      if (!hautDejaVisible && !entierementVisible) {
        element.scrollIntoView({
          behavior: "smooth",
          block: r.height > window.innerHeight * 0.6 ? "start" : "center",
          inline: "nearest",
        });
      }
    }
    placer();
    const t = setTimeout(placer, 420);
    return () => clearTimeout(t);
  }, [show, etape, placer]);

  // Le board défile horizontalement : on suit la cible plutôt que de la perdre.
  useEffect(() => {
    if (!show) {
      return undefined;
    }
    window.addEventListener("resize", placer);
    window.addEventListener("scroll", placer, true);
    return () => {
      window.removeEventListener("resize", placer);
      window.removeEventListener("scroll", placer, true);
    };
  }, [show, placer]);

  // Mesure la bulle à chaque étape : son contenu change, donc sa hauteur aussi.
  useLayoutEffect(() => {
    if (bulleRef.current) {
      const h = bulleRef.current.getBoundingClientRect().height;
      if (h && Math.abs(h - hauteurBulle) > 4) {
        setHauteurBulle(h);
      }
    }
  });

  const total = etapesVisibles.length;
  const dernier = index >= total - 1;

  const suivant = useCallback(() => {
    if (dernier) {
      onClose();
      return;
    }
    setIndex((i) => i + 1);
  }, [dernier, onClose]);

  const precedent = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    if (!show) {
      return undefined;
    }
    const auClavier = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") suivant();
      if (e.key === "ArrowLeft") precedent();
    };
    window.addEventListener("keydown", auClavier);
    return () => window.removeEventListener("keydown", auClavier);
  }, [show, onClose, suivant, precedent]);

  if (!show || !etape || total === 0) {
    return null;
  }

  // Placement de la bulle : on cherche le premier côté où elle tient
  // ENTIÈREMENT sans recouvrir la cible — dessous, dessus, droite, gauche —
  // et on retombe au centre si la cible occupe tout l'écran (vue d'ensemble).
  const marge = 14;
  let styleBulle;

  if (!rect) {
    styleBulle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  } else {
    const borneH = (v) =>
      Math.min(Math.max(12, v), Math.max(12, window.innerHeight - hauteurBulle - 12));
    const borneL = (v) =>
      Math.min(Math.max(12, v), Math.max(12, window.innerWidth - LARGEUR_BULLE - 12));

    const centreH = borneL(rect.left + rect.width / 2 - LARGEUR_BULLE / 2);
    const centreV = borneH(rect.top + rect.height / 2 - hauteurBulle / 2);

    const place = {
      bas: window.innerHeight - (rect.top + rect.height) - marge,
      haut: rect.top - marge,
      droite: window.innerWidth - (rect.left + rect.width) - marge,
      gauche: rect.left - marge,
    };

    const force = etape.placement;
    if ((force === "bas" || !force) && place.bas >= hauteurBulle) {
      styleBulle = { top: rect.top + rect.height + marge, left: centreH };
    } else if ((force === "haut" || !force) && place.haut >= hauteurBulle) {
      styleBulle = { top: rect.top - marge - hauteurBulle, left: centreH };
    } else if (place.bas >= hauteurBulle) {
      styleBulle = { top: rect.top + rect.height + marge, left: centreH };
    } else if (place.haut >= hauteurBulle) {
      styleBulle = { top: rect.top - marge - hauteurBulle, left: centreH };
    } else if (place.droite >= LARGEUR_BULLE) {
      styleBulle = { top: centreV, left: rect.left + rect.width + marge };
    } else if (place.gauche >= LARGEUR_BULLE) {
      styleBulle = { top: centreV, left: rect.left - marge - LARGEUR_BULLE };
    } else {
      styleBulle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    }
  }

  return (
    <>
      {/* Capte les clics pour que la visite ne soit pas interrompue par une
          action involontaire sur le board. */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 2000,
          cursor: "default",
        }}
        onClick={(e) => e.stopPropagation()}
      />

      {/* Le projecteur : un trou dans le voile, obtenu par une ombre géante. */}
      <div
        style={{
          position: "fixed",
          zIndex: 2001,
          pointerEvents: "none",
          borderRadius: 6,
          transition: "top .35s ease, left .35s ease, width .35s ease, height .35s ease",
          boxShadow: `0 0 0 9999px rgba(29, 29, 27, 0.55)`,
          outline: `2px solid ${COULEUR_PRIMAIRE}`,
          top: rect ? rect.top : window.innerHeight / 2,
          left: rect ? rect.left : window.innerWidth / 2,
          width: rect ? rect.width : 0,
          height: rect ? rect.height : 0,
        }}
      />

      <div
        ref={bulleRef}
        style={{
          position: "fixed",
          zIndex: 2002,
          width: LARGEUR_BULLE,
          maxWidth: "calc(100vw - 24px)",
          background: "#fff",
          color: COULEUR_TEXTE,
          borderRadius: 6,
          padding: "14px 16px",
          boxShadow: "0 4px 16px rgba(0,0,0,.18)",
          transition: "top .35s ease, left .35s ease",
          ...styleBulle,
        }}
      >
        <div className="d-flex justify-content-between align-items-start">
          <strong style={{ fontSize: "1rem", color: COULEUR_PRIMAIRE }}>{etape.titre}</strong>
          <Button
            variant="link"
            size="sm"
            className="p-0 ml-2"
            style={{ color: "#888", lineHeight: 1 }}
            onClick={onClose}
            title="Fermer la visite"
          >
            ✕
          </Button>
        </div>

        <div style={{ fontSize: "0.9rem", marginTop: 8 }}>{etape.texte}</div>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <small style={{ color: "#888" }}>
            Étape {index + 1} / {total}
          </small>
          <div>
            {index > 0 && (
              <Button variant="outline-secondary" size="sm" className="mr-2" onClick={precedent}>
                Précédent
              </Button>
            )}
            <Button variant="success" size="sm" onClick={suivant}>
              {dernier ? "J'ai compris" : "Suivant"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default VisiteGuidee;
